import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '@/store/authStore';
import { debug } from '@/utils/debug';
import { getErrorMessage } from '@/utils/errors';
import { features } from '@/config/features';
import type { LoginCredentials } from '@/types/auth';
import { usePrefetchCampaignsOnLogin } from './queries/useCampaignsQuery';
import { optimizationApi } from '@/services/api';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authStore = useAuthStore();
  const prefetchCampaigns = usePrefetchCampaignsOnLogin();

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      await authStore.login(credentials);

      // Get memberUuid from the store state (after login completed)
      const currentMemberUuid = useAuthStore.getState().memberUuid;

      // Fire-and-forget prefetches during login - don't block login completion
      // Data loads in background while user sees home page

      // Prefetch campaigns with fresh performance metrics (non-blocking)
      debug.log('Login: Starting campaigns prefetch (non-blocking)');
      prefetchCampaigns();

      // For refresh_on_login mode: also prefetch app configuration
      // Fire-and-forget during login to avoid blocking - config will load in background
      // while user is on home page, so it's ready when they navigate to campaigns
      debug.log('Login: Config prefetch check', {
        freshness: features.campaignDataFreshness,
        memberUuid: currentMemberUuid,
        willPrefetch: features.campaignDataFreshness === 'refresh_on_login' && !!currentMemberUuid
      });

      if (features.campaignDataFreshness === 'refresh_on_login' && currentMemberUuid) {
        debug.log('Login: Starting app configuration prefetch (non-blocking)');
        // Don't await - let config load in background while user sees home page
        // This replaces the backend background task that was removed from Google Sign-In
        queryClient.prefetchQuery({
          queryKey: ['app-config', currentMemberUuid],
          queryFn: () => optimizationApi.loadConfiguration(currentMemberUuid),
          staleTime: Infinity, // Cache forever (matches useAppConfigQuery for refresh_on_login)
        });
      } else {
        debug.log('Login: Skipping config prefetch', {
          reason: !currentMemberUuid ? 'no memberUuid' : 'not refresh_on_login mode'
        });
      }

      debug.log('Login: Complete - prefetches running in background');

      // Note: Navigation is handled by the Login component's redirect when isAuthenticated becomes true
      // We don't navigate here to avoid race conditions with user navigation after login

    } catch (error: unknown) {
      debug.error('Login failed:', error);

      const errorMessage = getErrorMessage(error, 'Login failed. Please check your credentials.');

      toast.error(errorMessage, {
        duration: 8000, // Show login errors longer for better readability
      });

      throw error; // Re-throw for form handling
    }
  }, [authStore, prefetchCampaigns, queryClient]);

  const logout = useCallback(() => {
    authStore.logout();

    toast('You have been logged out.', {
      icon: 'ℹ️',
    });

    navigate('/login', { replace: true });
  }, [authStore, navigate]);

  const checkAuth = useCallback(async () => {
    try {
      await authStore.checkExistingAuth();
    } catch (error) {
      debug.error('Auth check failed:', error);
      // Don't show notification for auth check failures
    }
  }, [authStore]);

  return {
    // State
    isAuthenticated: authStore.isAuthenticated,
    user: authStore.user,
    isLoading: authStore.isLoading,
    error: authStore.error,
    token: authStore.token,
    memberUuid: authStore.memberUuid,
    linkedAccountsReady: authStore.linkedAccountsReady,
    hasLinkedGoogleAccounts: authStore.hasLinkedGoogleAccounts,
    linkedAccountDetails: authStore.linkedAccountDetails,
    hasLinkedMetaAccounts: authStore.hasLinkedMetaAccounts,
    linkedMetaAccountDetails: authStore.linkedMetaAccountDetails,

    // Actions
    login,
    logout,
    checkAuth,
    clearError: authStore.clearError,
  };
}